import { Image } from '@timelyapp/react';
import classNames from 'classnames';
import type { TooltipPosition } from 'components/Tooltip';
import Tooltip from 'components/Tooltip';
import UserInitials from 'components/UserInitials';
import defaultImage from 'images/avatar/user_medium_retina.jpg';
import React, { useMemo } from 'react';
import styles from './Avatar.module.scss';

export interface AvatarProps {
  src?: string | null;
  name?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  shape?: 'square' | 'circle' | 'rounded';
  height?: number;
  width?: number;
  fallbackToInitials?: boolean;
  tooltipPosition?: TooltipPosition;
  tooltip?: boolean;
  style?: React.CSSProperties;
  center?: boolean;
}

const Avatar = ({
  className,
  fallbackToInitials = true,
  height,
  name,
  shape = 'square',
  size = 'medium',
  src,
  style,
  tooltip = true,
  tooltipPosition = 'top',
  width,
  center = false,
}: AvatarProps) => {
  const containerClass = classNames(
    className,
    styles.baseImage,
    styles[shape],
    {
      [styles[size]]: !width || !height,
    },
    'fs-exclude'
  );

  const initialsClass = classNames(containerClass, styles.initialsFallback);

  const fallback = useMemo(() => {
    return (
      fallbackToInitials && (
        <UserInitials className={initialsClass} name={name} style={{ width, height, ...style }} />
      )
    );
  }, [fallbackToInitials, height, initialsClass, name, style, width]);

  const image = (
    <Image
      style={{ width, height, ...style }}
      role="presentation"
      src={src || (!fallbackToInitials ? defaultImage : undefined)}
      className={containerClass}
      data-testid="image"
      fallback={fallback}
    />
  );

  return tooltip && name ? (
    <Tooltip position={tooltipPosition} title={name} className={center ? styles.center : undefined}>
      {image}
    </Tooltip>
  ) : (
    image
  );
};

export default Avatar;
